import { h, Component } from 'preact';
import cx from 'classnames';
import Portal from 'preact-portal';

import { MiniListing, Listing } from '../../../../types/listing.model';

import { toDecimal } from '../../../../core/decimal';
import { ListingEditorModal } from './listing-editor.modal';
import getStore, { appState$ } from '../../store';
import { GET_LISTING_DATA, GET_LISTING_DETAIL } from '../../reducers/listing/listing.actions';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { REQUEST_IN_PROGRESS, REQUEST_SUCCESS } from '../../../../types';

export interface OfflineListingEditorWidgetProps {
  listings?: Listing[];
  initialStage?: string;
}

export interface OfflineListingEditorWidgetState {
  listings?: Listing[];

  isCollapseVisible?: boolean;

  focusedListing?: Listing;
  isModalVisible?: boolean;

  isGettingListingDetail?: boolean;
  currentStage?: string;

}

export class OfflineListingEditorWidget extends Component<OfflineListingEditorWidgetProps, OfflineListingEditorWidgetState> {
  constructor(props: OfflineListingEditorWidgetProps) {
    super(props);

    if (props.listings) {
      this.setState({
        listings: props.listings
      });
    }

    if (props.initialStage) {
      this.setState({
        currentStage: props.initialStage
      });
    }

  }

  componentWillMount() {
    const listings$ = appState$.pipe(
      map(app => app.listing)
    );

    listings$.pipe(
      map(listing => ({
          requestState: listing.getListingDetailState,
          focusedListing: listing.focusedListing
        })
      ),
      distinctUntilChanged()
    ).subscribe(({requestState, focusedListing}) => {
      // state is for spinners

      if (requestState == REQUEST_IN_PROGRESS) {
        this.setState({
          isGettingListingDetail: true
        });
      } else {
        if (requestState == REQUEST_SUCCESS) {
          this.setState({
            focusedListing: focusedListing,
            isModalVisible: true
          });
        }

        this.setState({
          isGettingListingDetail: false
        })
      }
      
    });
  }

  componentWillReceiveProps(props: OfflineListingEditorWidgetProps) {
    this.setState({
      listings: props.listings
    });
  }

  toggleCollapsable = () => {
    this.setState({
      isCollapseVisible: !this.state.isCollapseVisible
    })
  }

  onPropertyUpdateClicked = (e, propertyKey) => {
    const store = getStore();
    store.dispatch({
      type: GET_LISTING_DETAIL,
      payload: {
        focusedPropertyKey: propertyKey
      }
    });
  }

  onModalClose = () => {
    this.setState({
      isModalVisible: false
    });
  }

  // I'm not a fan of this style as I prefer a reusable "agnostic" component
  // and by agnostic, I mean it's not tied to a parent or idea of a parent
  // component like this style
  // But anyway, please check HopplerModal for more info about the new modal system
  getPortalModal = (state: OfflineListingEditorWidgetState) => {
    if (state.isModalVisible) {
      return (
        <Portal into="body">
          <div className="hi-modal-container" id="hi-modal-111">
            <ListingEditorModal 
              close={this.onModalClose} 
              isOutsideClickClose={false} 
              containerId="hi-modal-111"
              listings={state.listings}
              focusedListing={state.focusedListing}
              currentStage={state.currentStage}
              onModalStageChanged={this.onListingStageClicked}
              />
          </div>
        </Portal>
      );
    }
  }

  onListingStageClicked = (stage: string, isFromModal?: boolean) => {
    const store = getStore();
    
    this.setState({
      currentStage: stage
    });

    store.dispatch({
      type: GET_LISTING_DATA,
      payload: {
        // focusedListing: null,
        gridStage: stage
      }
    });
    
    
  }

  render(props: OfflineListingEditorWidgetProps, state: OfflineListingEditorWidgetState) {
    return (
      <div className="hi-offline-listing-editor-widget hi-widget-collapsable">
        { this.getPortalModal(state) }
        <div className="row hi-widget-collapsable__toggle-row">
          <div className="col-10">
            <h4 className="hi-offline-listing-editor-widget__name">
              Offline Listings to Update
            </h4>
          </div>
          <div className="col-2 d-md-none">
            <i className={ cx('fas fa-chevron-down', {
                'visible': state.isCollapseVisible
              })}
              onClick={ this.toggleCollapsable }>
            </i>
          </div>
        </div>

        <div className={ cx('hi-widget-collapsable__body', {
          'visible': state.isCollapseVisible
        }) }>

          <div className="row">
            <div className="col-12">
              <p className="hi-offline-listing-editor-widget__desc">
                { props.listings.length } Listings
              </p>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-12">
              <button onClick={e => this.onListingStageClicked('pending')}
                className={ cx(
                  'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                  {
                    active: state.currentStage === 'pending'
                  }
                )}>
                Pending
              </button>
              <button onClick={e => this.onListingStageClicked('drafts')}
                className={ cx(
                  'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                  {
                    active: state.currentStage === 'drafts'
                  }
                )}>
                Drafts
              </button>
              <button onClick={e => this.onListingStageClicked('declined')}
                className={ cx(
                  'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                  {
                    active: state.currentStage === 'declined'
                  }
                )}>
                Declined
              </button>
              <button onClick={e => this.onListingStageClicked('expired')}
                className={ cx(
                  'btn hi-offline-listing-editor-widget__cat-toggle mb-2', 
                  {
                    active: state.currentStage === 'expired'
                  }
                )}>
                Expired
              </button>
            </div>
          </div>

          <div className="row hi-offline-listing-editor-widget__listings mt-0 mt-lg-4">
            {
              state.listings.map((listing, index) => {
                return (
                  <div className="col-12 hi-offline-listing-editor-widget__listing">
                    <a href="#" onClick={ (e) => this.onPropertyUpdateClicked(e, listing.propertyKey) }
                      className="hi-offline-listing-editor-widget__listing__update-btn">
                      Update <i className="fas fa-chevron-right"></i>
                    </a>
                    <p className="hi-offline-listing-editor-widget__listing__property-key">
                      { listing.propertyKey }
                    </p>
                    <p className="hi-offline-listing-editor-widget__listing__cat-type">
                      { listing.type } for { listing.category }
                    </p>
                    <p className="hi-offline-listing-editor-widget__listing__address">
                      { listing.address }
                    </p>
                    {
                      listing.minPrice ?
                      <p className="hi-offline-listing-editor-widget__listing__price">
                        Php { toDecimal(listing.minPrice) }{ listing.category == 'Rent' ? '/month' : '' }
                      </p> : null
                    }
                  </div>
                );
              })
            }

            {
              !state.listings || state.listings.length < 1 ?
              <div className="w-100 h-100 text-center">
                <p>
                  No available listing data
                </p>
              </div> : null
            }
          </div>

        </div>

      </div>
    );
  }
}